import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import apiClient from '../../../lib/apiClient';

/**
 * Hook for fetching and managing the Zero Trust Product Catalog.
 *
 * This is the single source of truth for ZT products, replacing hardcoded data in:
 * - productMaturityMappings.js (PRODUCT_MATURITY_MAPPINGS)
 * - JourneyMapView.js (UPGRADE_GUIDANCE, PRODUCT_SUGGESTIONS)
 * - PillarTemplate.js (SAMPLE_PRODUCTS)
 *
 * @param {Object} filters - Optional filters (pillar, category, active)
 * @returns {Object} Product catalog state and helper functions
 */
export function useZtProductCatalog(filters = {}) {
  const queryClient = useQueryClient();

  const {
    data: catalogData,
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: ['zt', 'products-with-configs', filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters.pillar) params.set('pillar', filters.pillar);
      if (filters.category) params.set('category', filters.category);
      if (filters.active !== undefined) params.set('active', String(filters.active));

      const queryString = params.toString();
      const url = queryString ? `/api/zt/products-with-configs?${queryString}` : '/api/zt/products-with-configs';

      return apiClient.get(url);
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 30 * 60 * 1000, // 30 minutes
    refetchOnWindowFocus: false
  });

  const products = catalogData?.products || [];
  const categories = catalogData?.categories || [];

  // Build indexed lookups for efficient access
  const productsByCode = useMemo(() => {
    const map = {};
    for (const product of products) {
      map[product.product_code] = product;
    }
    return map;
  }, [products]);

  const productsByCategory = useMemo(() => {
    const map = {};
    for (const product of products) {
      if (!map[product.category]) {
        map[product.category] = [];
      }
      map[product.category].push(product);
    }
    return map;
  }, [products]);

  const productsByPillar = useMemo(() => {
    const map = {};
    for (const product of products) {
      if (!map[product.pillar_code]) {
        map[product.pillar_code] = [];
      }
      map[product.pillar_code].push(product);
    }
    return map;
  }, [products]);

  const productsByVendor = useMemo(() => {
    const map = {};
    for (const product of products) {
      const vendor = product.vendor?.toLowerCase();
      if (!vendor) continue;
      if (!map[vendor]) {
        map[vendor] = [];
      }
      map[vendor].push(product);
    }
    return map;
  }, [products]);

  // Get a single product by code
  const getProduct = useCallback((productCode) => {
    return productsByCode[productCode] || null;
  }, [productsByCode]);

  // Get products for a category
  const getProductsByCategory = useCallback((categoryCode) => {
    return productsByCategory[categoryCode] || [];
  }, [productsByCategory]);

  // Get products for a pillar
  const getProductsByPillar = useCallback((pillarCode) => {
    return productsByPillar[pillarCode] || [];
  }, [productsByPillar]);

  // Get products for a vendor
  const getProductsByVendor = useCallback((vendorName) => {
    return productsByVendor[vendorName?.toLowerCase()] || [];
  }, [productsByVendor]);

  // Get products that contribute to a specific CISA function
  const getProductsForFunction = useCallback((functionCode) => {
    return products.filter(
      p => p.contributes_to?.includes(functionCode)
    );
  }, [products]);

  // Get category lookup by code
  const getCategoryInfo = useCallback((categoryCode) => {
    return categories.find(c => c.category_code === categoryCode) || null;
  }, [categories]);

  // Prefetch a single product (useful before opening details modal)
  const prefetchProduct = useCallback((productCode) => {
    queryClient.prefetchQuery({
      queryKey: ['zt', 'product', productCode],
      queryFn: () => apiClient.get(`/api/zt/products/${productCode}`),
      staleTime: 5 * 60 * 1000
    });
  }, [queryClient]);

  // Convert to PRODUCT_MATURITY_MAPPINGS format for backward compatibility
  const asMaturityMappings = useMemo(() => {
    const mappings = {};
    for (const product of products) {
      mappings[product.product_code] = {
        id: product.product_code,
        name: product.name,
        vendor: product.vendor,
        category: product.category,
        icon: product.icon,
        description: product.description,
        contributesTo: product.contributes_to || [],
        configurations: (product.configurations || []).map(cfg => ({
          id: cfg.config_code,
          label: cfg.label,
          shortLabel: cfg.short_label,
          icon: cfg.icon,
          description: cfg.description,
          criteria: cfg.criteria,
          upgradeHint: cfg.upgrade_hint,
          maturityMapping: (cfg.maturity_mappings || []).reduce((acc, m) => {
            acc[m.function_code] = m.maturity_level;
            return acc;
          }, {})
        }))
      };
    }
    return mappings;
  }, [products]);

  // Convert to PRODUCT_CATEGORIES format for backward compatibility
  const asCategoriesLookup = useMemo(() => {
    const lookup = {};
    for (const cat of categories) {
      lookup[cat.category_code] = {
        code: cat.category_code,
        label: cat.label,
        icon: cat.icon,
        pillarCode: cat.pillar_code
      };
    }
    return lookup;
  }, [categories]);

  return {
    // Raw data
    products,
    categories,
    isLoading,
    error,
    refetch,

    // Lookup helpers
    getProduct,
    getProductsByCategory,
    getProductsByPillar,
    getProductsByVendor,
    getProductsForFunction,
    getCategoryInfo,
    prefetchProduct,

    // Backward-compatible formats
    asMaturityMappings,
    asCategoriesLookup
  };
}

/**
 * Hook for fetching product suggestions for a CISA function.
 *
 * @param {string} functionCode - The CISA function code (e.g., 'ID_AUTH')
 * @returns {Object} Suggestions grouped by maturity level
 */
export function useZtFunctionSuggestions(functionCode) {
  const {
    data,
    isLoading,
    error
  } = useQuery({
    queryKey: ['zt', 'function-suggestions', functionCode],
    queryFn: () => apiClient.get(`/api/zt/function-suggestions/${functionCode}`),
    staleTime: 5 * 60 * 1000,
    enabled: !!functionCode
  });

  return {
    functionCode: data?.function_code || functionCode,
    suggestions: data?.suggestions || {},
    isLoading,
    error
  };
}

/**
 * Hook for fetching upgrade guidance for a specific product.
 *
 * @param {string} productCode - The product code (e.g., 'duo-security')
 * @param {string} targetLevel - Target maturity level ('ADVANCED' or 'OPTIMAL')
 * @returns {Object} Upgrade guidance data
 */
export function useZtUpgradeGuidance(productCode, targetLevel) {
  const {
    data,
    isLoading,
    error
  } = useQuery({
    queryKey: ['zt', 'upgrade-guidance', productCode, targetLevel],
    queryFn: () => apiClient.get(`/api/zt/products/${productCode}/upgrade-guidance/${targetLevel}`),
    staleTime: 5 * 60 * 1000,
    enabled: !!productCode && !!targetLevel
  });

  return {
    guidance: data || null,
    isLoading,
    error
  };
}

/**
 * Hook for fetching product categories.
 *
 * @returns {Object} Categories data and loading state
 */
export function useZtCategories() {
  const {
    data,
    isLoading,
    error
  } = useQuery({
    queryKey: ['zt', 'categories'],
    queryFn: () => apiClient.get('/api/zt/categories'),
    staleTime: 10 * 60 * 1000, // 10 minutes - categories rarely change
    cacheTime: 60 * 60 * 1000 // 1 hour
  });

  // Group categories by pillar
  const categoriesByPillar = useMemo(() => {
    const grouped = {};
    for (const cat of (data || [])) {
      if (!grouped[cat.pillar_code]) {
        grouped[cat.pillar_code] = [];
      }
      grouped[cat.pillar_code].push(cat);
    }
    return grouped;
  }, [data]);

  return {
    categories: data || [],
    categoriesByPillar,
    isLoading,
    error
  };
}

/**
 * Hook for getting upgrade guidance with fallback to local data.
 * Matches the UPGRADE_GUIDANCE structure from JourneyMapView.js.
 *
 * @param {string} productName - The product display name (e.g., 'Duo Security')
 * @param {string} targetLevel - 'ADVANCED' or 'OPTIMAL'
 * @returns {Object} Guidance in the format { canUpgrade, steps, effort, timeline, ... }
 */
export function useUpgradeGuidanceForProduct(productName, targetLevel) {
  // Try to find product code from name
  const { products } = useZtProductCatalog();

  const productCode = useMemo(() => {
    const product = products.find(
      p => p.name?.toLowerCase() === productName?.toLowerCase()
    );
    return product?.product_code || null;
  }, [products, productName]);

  const { guidance, isLoading, error } = useZtUpgradeGuidance(productCode, targetLevel);

  // Transform API response to match local UPGRADE_GUIDANCE format
  const transformedGuidance = useMemo(() => {
    if (!guidance) return null;

    return {
      canUpgrade: guidance.can_upgrade,
      needsProductChange: guidance.needs_product_change,
      alreadyMeets: guidance.already_meets,
      isLegacy: guidance.is_legacy,
      steps: guidance.steps || [],
      additionalProducts: guidance.additional_products || [],
      effort: guidance.effort,
      timeline: guidance.timeline
    };
  }, [guidance]);

  return {
    guidance: transformedGuidance,
    isLoading,
    error,
    hasData: !!guidance && guidance.steps?.length > 0
  };
}

export default useZtProductCatalog;
